import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KPI } from "@/components/DashboardWidgets";
import {
  BarChart3, Wallet, CheckCircle2, AlertCircle, GraduationCap, Users, TrendingUp,
  Percent, CalendarDays, Award, Filter, RefreshCw, MessageCircle, User,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const inr = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;
const ATT_COLORS = { present: "#059669", absent: "#dc2626", late: "#d97706" };
const PIE_COLORS = ["#2563eb", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#ea580c"];
const GRADE_COLORS = { "A+": "#059669", "A": "#22c55e", "B": "#0ea5e9", "C": "#6366f1", "D": "#f59e0b", "E": "#f97316", "F": "#dc2626" };

const todayIso = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => todayIso().slice(0, 8) + "01";

const EmptyState = ({ msg }) => (
  <div className="text-center py-12 text-slate-400 text-sm" data-testid="report-empty">
    <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
    {msg || "No data for the selected filters"}
  </div>
);

export default function ReportsCentre() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const [tab, setTab] = useState(sp.get("tab") || "attendance");
  const [classFilter, setClassFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attPage, setAttPage] = useState(1);
  useEffect(() => { setAttPage(1); }, [classFilter, dateFrom, dateTo]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (classFilter && classFilter !== "all") params.set("class_name", classFilter);
      params.set("date_from", dateFrom);
      params.set("date_to", dateTo);
      const r = await api.get(`/reports/summary?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classFilter, dateFrom, dateTo]);

  useEffect(() => { setSp({ tab }, { replace: true }); /* eslint-disable-next-line */ }, [tab]);

  const classes = data?.classes || [];
  const A = data?.attendance;
  const F = data?.fees;
  const AC = data?.academics;

  return (
    <DashLayout variant="school" title="Reports Centre">
      {/* ---- Filter Bar ---- */}
      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="reports-filter-bar">
        <div className="flex items-center gap-2 text-slate-500 text-sm mr-2">
          <Filter className="w-4 h-4" /> Filters
        </div>
        <div>
          <Label className="text-xs text-slate-500">Class</Label>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-40" data-testid="filter-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-class-all">All Classes</SelectItem>
              {classes.map((c) => (
                <SelectItem key={c} value={c} data-testid={`filter-class-${c}`}>Class {c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Date From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Date To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="filter-date-to" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="filter-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <div className="ml-auto text-xs text-slate-500">
          {data ? (
            <>Range: <b className="text-slate-700">{data.filters.date_from}</b> → <b className="text-slate-700">{data.filters.date_to}</b> · Class: <b className="text-slate-700">{data.filters.class_name === "all" ? "All" : `Class ${data.filters.class_name}`}</b></>
          ) : loading ? "Loading…" : ""}
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="attendance" data-testid="tab-attendance"><ClipTab icon={CalendarDays} label="Attendance" /></TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees"><ClipTab icon={Wallet} label="Fees" /></TabsTrigger>
          <TabsTrigger value="academics" data-testid="tab-academics"><ClipTab icon={GraduationCap} label="Academics" /></TabsTrigger>
        </TabsList>

        {/* ============== ATTENDANCE ============== */}
        <TabsContent value="attendance">
          {!A ? <EmptyState msg="Loading…" /> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <KPI icon={CheckCircle2} label="Attendance %" value={`${A.kpis.percent}%`} color="text-emerald-700 bg-emerald-50" testId="a-kpi-percent" />
                <KPI icon={Users} label="Marked" value={A.kpis.marked} color="text-blue-700 bg-blue-50" testId="a-kpi-marked" />
                <KPI icon={CheckCircle2} label="Present" value={A.kpis.present} color="text-emerald-700 bg-emerald-50" testId="a-kpi-present" />
                <KPI icon={AlertCircle} label="Absent" value={A.kpis.absent} color="text-red-700 bg-red-50" testId="a-kpi-absent" />
                <KPI icon={AlertCircle} label="Late" value={A.kpis.late} color="text-amber-700 bg-amber-50" testId="a-kpi-late" />
              </div>

              {A.trend.length === 0 ? <EmptyState msg="No attendance marked in this range" /> : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                  <Card className="p-4 lg:col-span-2" data-testid="a-trend-card">
                    <h3 className="font-bold mb-2 text-sm">Daily Attendance Trend</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={A.trend} margin={{ top: 5, right: 12, left: -12, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" fontSize={11} />
                        <YAxis fontSize={11} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="present" stroke={ATT_COLORS.present} strokeWidth={2} name="Present" />
                        <Line type="monotone" dataKey="absent" stroke={ATT_COLORS.absent} strokeWidth={2} name="Absent" />
                        <Line type="monotone" dataKey="late" stroke={ATT_COLORS.late} strokeWidth={2} name="Late" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>

                  <Card className="p-4" data-testid="a-mix-card">
                    <h3 className="font-bold mb-2 text-sm">Overall Mix</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Present", value: A.kpis.present },
                            { name: "Absent", value: A.kpis.absent },
                            { name: "Late", value: A.kpis.late },
                          ]}
                          cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label
                        >
                          <Cell fill={ATT_COLORS.present} />
                          <Cell fill={ATT_COLORS.absent} />
                          <Cell fill={ATT_COLORS.late} />
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                </div>
              )}

              {A.by_class.length > 1 && (
                <Card className="p-4 mb-4" data-testid="a-byclass-card">
                  <h3 className="font-bold mb-2 text-sm">Attendance by Class</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={A.by_class} margin={{ top: 5, right: 12, left: -12, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="class" fontSize={11} />
                      <YAxis fontSize={11} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="present" stackId="a" fill={ATT_COLORS.present} name="Present" />
                      <Bar dataKey="absent" stackId="a" fill={ATT_COLORS.absent} name="Absent" />
                      <Bar dataKey="late" stackId="a" fill={ATT_COLORS.late} name="Late" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              <Card className="p-0 overflow-hidden" data-testid="a-students-card">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-sm">Student-wise Attendance</h3>
                  <Badge variant="outline">{A.students.length} students</Badge>
                </div>
                {A.students.length === 0 ? <EmptyState msg="No student attendance in this range" /> : (() => {
                  const PER = 20;
                  const total = A.students.length;
                  const totalPages = Math.max(1, Math.ceil(total / PER));
                  const p = Math.min(attPage, totalPages);
                  const slice = A.students.slice((p - 1) * PER, p * PER);
                  return (
                    <>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead className="text-right">Marked</TableHead>
                          <TableHead className="text-right">Present</TableHead>
                          <TableHead className="text-right">Absent</TableHead>
                          <TableHead className="text-right">Late</TableHead>
                          <TableHead className="text-right">%</TableHead>
                          <TableHead className="text-right">—</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {slice.map((s) => (
                            <TableRow key={s.student_id} data-testid={`a-stu-${s.student_id}`}>
                              <TableCell className="font-semibold">{s.name}</TableCell>
                              <TableCell>{s.class_name}{s.section ? ` ${s.section}` : ""}</TableCell>
                              <TableCell className="text-right tabular-nums">{s.marked}</TableCell>
                              <TableCell className="text-right tabular-nums text-emerald-700">{s.present}</TableCell>
                              <TableCell className="text-right tabular-nums text-red-700">{s.absent}</TableCell>
                              <TableCell className="text-right tabular-nums text-amber-700">{s.late}</TableCell>
                              <TableCell className="text-right tabular-nums font-bold">
                                <Badge variant="outline" className={s.percent >= 75 ? "border-emerald-300 text-emerald-700" : s.percent >= 50 ? "border-amber-300 text-amber-700" : "border-red-300 text-red-700"}>
                                  {s.percent}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button size="sm" variant="ghost" onClick={() => nav(`/school/students/${s.student_id}`)} data-testid={`a-stu-open-${s.student_id}`} title="Open profile"><User className="w-4 h-4" /></Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {total > PER && (
                        <div className="flex items-center justify-between p-3 border-t bg-slate-50" data-testid="a-stu-pagination">
                          <Button variant="outline" size="sm" disabled={p <= 1} onClick={() => setAttPage(p - 1)} data-testid="a-stu-prev">‹ Prev</Button>
                          <div className="text-xs text-slate-500">Page <b className="text-slate-800">{p}</b> of {totalPages} · showing {slice.length} of {total}</div>
                          <Button variant="outline" size="sm" disabled={p >= totalPages} onClick={() => setAttPage(p + 1)} data-testid="a-stu-next">Next ›</Button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </Card>
            </>
          )}
        </TabsContent>

        {/* ============== FEES ============== */}
        <TabsContent value="fees">
          {!F ? <EmptyState msg="Loading…" /> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <KPI icon={Wallet} label="Billed" value={inr(F.kpis.billed)} color="text-blue-700 bg-blue-50" testId="f-kpi-billed" />
                <KPI icon={CheckCircle2} label="Collected" value={inr(F.kpis.paid)} color="text-emerald-700 bg-emerald-50" testId="f-kpi-paid" />
                <KPI icon={AlertCircle} label="Total Due" value={inr(F.kpis.due)} color="text-red-700 bg-red-50" testId="f-kpi-due" />
                <KPI icon={Percent} label="Collection Rate" value={`${F.kpis.collection_rate}%`} color="text-purple-700 bg-purple-50" testId="f-kpi-rate" />
                <KPI icon={TrendingUp} label="Payments" value={F.kpis.payments} color="text-indigo-700 bg-indigo-50" testId="f-kpi-payments" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <Card className="p-4 lg:col-span-2" data-testid="f-trend-card">
                  <h3 className="font-bold mb-2 text-sm">Daily Collection Trend</h3>
                  {F.collection_trend.length === 0 ? <EmptyState msg="No payments in this range" /> : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={F.collection_trend} margin={{ top: 5, right: 12, left: -12, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" fontSize={11} />
                        <YAxis fontSize={11} tickFormatter={(v) => `₹${v/1000}k`} />
                        <Tooltip formatter={(v) => inr(v)} />
                        <Bar dataKey="amount" fill="#059669" name="Collected" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>

                <Card className="p-4" data-testid="f-mode-card">
                  <h3 className="font-bold mb-2 text-sm">Payment Modes</h3>
                  {F.payment_modes.length === 0 ? <EmptyState msg="No payments" /> : (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={F.payment_modes} cx="50%" cy="50%" outerRadius={90} dataKey="amount" nameKey="mode" label={(e) => e.mode}>
                          {F.payment_modes.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => inr(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>

              <Card className="p-0 overflow-hidden" data-testid="f-dues-card">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-sm">Top Dues</h3>
                  <Badge variant="outline">{F.dues.length} students</Badge>
                </div>
                {F.dues.length === 0 ? <EmptyState msg="Every student is settled — great job" /> : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead className="text-right">Billed</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Due</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {F.dues.map((d) => {
                        const phone = String(d.mobile || "").replace(/\D/g, "");
                        const msg = `Dear Parent,\n\nGentle reminder — pending school fees for ${d.name} (Class ${d.class_name || ""}${d.section ? " " + d.section : ""}).\n\nOutstanding: *₹${(d.due).toLocaleString("en-IN")}*\n\nKindly clear the dues at your earliest convenience.\n\nThank you.`;
                        const waUrl = phone ? `https://wa.me/${phone.length > 10 ? phone : "91" + phone}?text=${encodeURIComponent(msg)}` : "";
                        return (
                        <TableRow key={d.student_id} data-testid={`f-due-${d.student_id}`}>
                          <TableCell className="font-semibold">{d.name}</TableCell>
                          <TableCell>{d.class_name}{d.section ? ` ${d.section}` : ""}</TableCell>
                          <TableCell className="text-right tabular-nums">{inr(d.billed)}</TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-700">{inr(d.paid)}</TableCell>
                          <TableCell className="text-right tabular-nums text-red-700 font-bold">{inr(d.due)}</TableCell>
                          <TableCell className="text-right space-x-1 whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => nav(`/school/students/${d.student_id}`)} data-testid={`f-due-profile-${d.student_id}`} title="Open student profile"><User className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" disabled={!waUrl} onClick={() => waUrl && window.open(waUrl, "_blank")} data-testid={`f-due-wa-${d.student_id}`} title={waUrl ? "WhatsApp reminder" : "No parent mobile on file"}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button>
                          </TableCell>
                        </TableRow>
                      );})}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </>
          )}
        </TabsContent>

        {/* ============== ACADEMICS ============== */}
        <TabsContent value="academics">
          {!AC ? <EmptyState msg="Loading…" /> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <KPI icon={Award} label="Exams" value={AC.kpis.exams} color="text-blue-700 bg-blue-50" testId="ac-kpi-exams" />
                <KPI icon={Users} label="Students Evaluated" value={AC.kpis.students_evaluated} color="text-indigo-700 bg-indigo-50" testId="ac-kpi-eval" />
                <KPI icon={Percent} label="Avg Score" value={`${AC.kpis.avg_percent}%`} color="text-emerald-700 bg-emerald-50" testId="ac-kpi-avg" />
                <KPI icon={CheckCircle2} label="Pass Rate" value={`${AC.kpis.pass_rate}%`} color="text-emerald-700 bg-emerald-50" testId="ac-kpi-pass" />
                <KPI icon={GraduationCap} label="Results" value={AC.kpis.results_count} color="text-purple-700 bg-purple-50" testId="ac-kpi-results" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <Card className="p-4 lg:col-span-2" data-testid="ac-grades-card">
                  <h3 className="font-bold mb-2 text-sm">Grade Distribution</h3>
                  {AC.kpis.results_count === 0 ? <EmptyState msg="No results in this range" /> : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={AC.grade_distribution} margin={{ top: 5, right: 12, left: -12, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="grade" fontSize={12} />
                        <YAxis fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="count" name="Students">
                          {AC.grade_distribution.map((g, i) => (
                            <Cell key={i} fill={GRADE_COLORS[g.grade] || "#64748b"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>

                <Card className="p-4" data-testid="ac-pie-card">
                  <h3 className="font-bold mb-2 text-sm">Grade Share</h3>
                  {AC.kpis.results_count === 0 ? <EmptyState msg="No results" /> : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={AC.grade_distribution.filter(g => g.count > 0)} cx="50%" cy="50%" outerRadius={95} dataKey="count" nameKey="grade" label={(e) => e.grade}>
                          {AC.grade_distribution.map((g, i) => (
                            <Cell key={i} fill={GRADE_COLORS[g.grade] || "#64748b"} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>

              <Card className="p-0 overflow-hidden mb-4" data-testid="ac-exams-card">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-sm">Exam-wise Performance</h3>
                  <Badge variant="outline">{AC.exams.length} exams</Badge>
                </div>
                {AC.exams.length === 0 ? <EmptyState msg="No exams in this range" /> : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Exam</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Students</TableHead>
                      <TableHead className="text-right">Avg %</TableHead>
                      <TableHead className="text-right">Highest</TableHead>
                      <TableHead className="text-right">Lowest</TableHead>
                      <TableHead className="text-right">Pass Rate</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {AC.exams.map((ex) => (
                        <TableRow key={ex.exam_id} data-testid={`ac-exam-${ex.exam_id}`}>
                          <TableCell className="font-semibold">{ex.name}</TableCell>
                          <TableCell>{ex.class_name}{ex.section ? ` ${ex.section}` : ""}</TableCell>
                          <TableCell className="font-mono text-xs">{ex.exam_date}</TableCell>
                          <TableCell className="text-right tabular-nums">{ex.students}</TableCell>
                          <TableCell className="text-right tabular-nums font-bold">{ex.avg_percent}%</TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-700">{ex.highest}%</TableCell>
                          <TableCell className="text-right tabular-nums text-red-700">{ex.lowest}%</TableCell>
                          <TableCell className="text-right tabular-nums">
                            <Badge variant="outline" className={ex.pass_rate >= 75 ? "border-emerald-300 text-emerald-700" : ex.pass_rate >= 50 ? "border-amber-300 text-amber-700" : "border-red-300 text-red-700"}>
                              {ex.pass_rate}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              <Card className="p-0 overflow-hidden" data-testid="ac-top-card">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-sm">Top Students</h3>
                  <Badge variant="outline">{AC.top_students.length} students</Badge>
                </div>
                {AC.top_students.length === 0 ? <EmptyState msg="No student results in this range" /> : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="w-14">#</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead className="text-right">Exams</TableHead>
                      <TableHead className="text-right">Avg %</TableHead>
                      <TableHead className="text-right">Grade</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {AC.top_students.map((s, i) => (
                        <TableRow key={s.student_id} data-testid={`ac-top-${s.student_id}`}>
                          <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                          <TableCell className="font-semibold">{s.name}</TableCell>
                          <TableCell>{s.class_name}{s.section ? ` ${s.section}` : ""}</TableCell>
                          <TableCell className="text-right tabular-nums">{s.exams}</TableCell>
                          <TableCell className="text-right tabular-nums font-bold">{s.avg_percent}%</TableCell>
                          <TableCell className="text-right">
                            <Badge className="text-white" style={{ background: GRADE_COLORS[s.grade] || "#64748b" }}>
                              {s.grade}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </DashLayout>
  );
}

const ClipTab = ({ icon: Icon, label }) => (
  <span className="flex items-center gap-1.5"><Icon className="w-4 h-4" /> {label}</span>
);
